import React, { useState, useEffect, useCallback } from 'react';
import prodConfig from '../config/prodProductPerformance.json';
import countryCodeMapping from '../config/countryCodeMapping.json';
import { graphqlQuery, graphqlMutation } from '../utils/graphqlApi';
import { getProdPerformance, getTribeSummaryQuery, getTribePerformance } from '../graphql/queries';
import { analyzeProdPerformance, getTribeSummary, analyzeTribePerformance } from '../graphql/mutations';
import useUserPermissions from '../hooks/useUserPermissions';

const TIME_RANGES = [
  { value: 'current_week', label: 'Current Week' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'past_month', label: 'Past Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
];

// Tribe list aligned with appConfig (Incident Resolver page)
const TRIBES = Array.isArray(prodConfig.tribes) ? prodConfig.tribes : [];

// Get service offerings for a tribe from prodConfig
const getOfferingsForTribe = (tribe) => {
  if (prodConfig.solutions[tribe]) {
    return Object.keys(prodConfig.solutions[tribe]);
  }
  return [];
};

const getServiceOfferingValue = (tribe, offeringKey) => {
  return prodConfig.solutions[tribe]?.[offeringKey] || offeringKey;
};

const parseJsonSafely = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string') {
      try { return JSON.parse(parsed); } catch { return parsed; }
    }
    return parsed;
  } catch (parseError) {
    console.error('[ProdPerformance] Failed to parse JSON payload:', parseError);
    return null;
  }
};

const getAnalysisError = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return payload.errorMessage || payload.message || payload.error || null;
};

const normalizeAnalysis = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nestedAnalysis = payload.bedrock_analysis
    ? parseJsonSafely(payload.bedrock_analysis)
    : null;
  const analysis = nestedAnalysis && typeof nestedAnalysis === 'object'
    ? nestedAnalysis
    : payload;

  const topCountries = Array.isArray(analysis.top_5_countries) ? analysis.top_5_countries : [];
  const recurringIncidents = Array.isArray(analysis.top_3_recurring_incidents)
    ? analysis.top_3_recurring_incidents : [];
  const longestResolveTimes = Array.isArray(analysis.top_5_longest_resolve_times)
    ? analysis.top_5_longest_resolve_times : [];

  // total_incidents is always present in the Bedrock response
  const totalIncidents = analysis.total_incidents ?? 0;

  // Compute avg resolution time from top_5_longest_resolve_times
  let avgResolutionHours = null;
  if (longestResolveTimes.length > 0) {
    const parsedHours = longestResolveTimes.map(item => {
      const rt = (item.resolve_time || '').toLowerCase();
      let h = 0;
      const daysMatch = rt.match(/([\d.]+)\s*day/);
      const hoursMatch = rt.match(/([\d.]+)\s*hour/);
      const minsMatch = rt.match(/([\d.]+)\s*min/);
      if (daysMatch) h += parseFloat(daysMatch[1]) * 24;
      if (hoursMatch) h += parseFloat(hoursMatch[1]);
      if (minsMatch) h += parseFloat(minsMatch[1]) / 60;
      return h > 0 ? h : null;
    }).filter(Boolean);
    if (parsedHours.length > 0) {
      avgResolutionHours = Math.round((parsedHours.reduce((a, b) => a + b, 0) / parsedHours.length) * 10) / 10;
    }
  }

  // Count SLA breaches from top_5_longest_resolve_times
  const slaBreach = longestResolveTimes.filter(item =>
    item.sla_breached === true || item.sla_breached === 'Yes' || item.sla_breached === 'yes'
  ).length;

  // Sum recurring incident counts from top_3_recurring_incidents
  const recurringCount = recurringIncidents.reduce(
    (sum, item) => sum + (item.count ?? item.frequency ?? 0), 0
  );

  return {
    total_incidents: totalIncidents,
    avg_resolution_time_hours: avgResolutionHours,
    sla_breached_count: slaBreach,
    recurring_incident_count: recurringCount,
    top_5_countries: topCountries,
    top_3_recurring_incidents: recurringIncidents,
    top_5_longest_resolve_times: longestResolveTimes,
  };
};

// Helper to get SLA color based on count
const getSlaColor = (count) => {
  if (count <= 2) return '#10B981'; // green
  if (count <= 7) return '#F59E0B'; // yellow/orange
  return '#EF4444'; // red
};

// Format hours into "X D Y Hrs" format
const formatResolutionTime = (hours) => {
  if (hours == null) return 'N/A';
  if (hours < 1) return `${Math.round(hours * 60)} Mins`;
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  const remainingMins = Math.round((hours % 1) * 60);
  if (days > 0) {
    if (remainingHours === 0) return `${days} D`;
    return `${days} D ${remainingHours} Hrs`;
  }
  if (remainingMins === 0) return `${remainingHours} Hrs`;
  return `${remainingHours} Hrs ${remainingMins} Mins`;
};

function ProdProductPerformance() {
  const {
    canAccessProdPerformance,
    getUserRole,
    isLoading: isPermissionsLoading,
  } = useUserPermissions();

  // --- Section 1: Time Period ---
  const [timeRange, setTimeRange] = useState('current_month');

  // --- Section 2: Tribe Summary Cards ---
  const [tribeSummaries, setTribeSummaries] = useState({});
  const [isTribeSummaryLoading, setIsTribeSummaryLoading] = useState(false);
  const [tribeSummaryError, setTribeSummaryError] = useState(null);

  // --- Section 3: Drill-Down ---
  const [selectedTribe, setSelectedTribe] = useState('');
  const [selectedOffering, setSelectedOffering] = useState('');
  const [offerings, setOfferings] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);
  const [drillDownError, setDrillDownError] = useState(null);

  // Fetch tribe summary cards — single API call for all tribes
  const fetchTribeSummaries = useCallback(async (timeline) => {
    setIsTribeSummaryLoading(true);
    setTribeSummaryError(null);
    setTribeSummaries({});

    try {
      let responseBody = null;

      // Try query first (cached/fast path)
      try {
        const queryResult = await graphqlQuery(getTribeSummaryQuery, { time_range: timeline });
        responseBody = queryResult?.getTribeSummary?.body;
      } catch (queryErr) {
        console.warn('[ProdPerformance] getTribeSummary query failed, trying mutation:', queryErr.message);
      }

      // Fall back to mutation (generates fresh data)
      if (!responseBody) {
        const mutationResult = await graphqlMutation(getTribeSummary, {
          input: { time_range: timeline },
        });
        responseBody = mutationResult?.generateTribeSummary?.body;
      }

      if (!responseBody) {
        setTribeSummaryError('No tribe summary data returned.');
        return;
      }

      const parsed = parseJsonSafely(responseBody);
      if (!parsed) {
        setTribeSummaryError('Failed to parse tribe summary response.');
        return;
      }

      // Lambda returns { tribe_summary: { DXI: {...}, FSS: {...}, ... } }
      const tribeSummaryData = parsed.tribe_summary || parsed;

      const summaryMap = {};
      TRIBES.forEach((t) => {
        if (tribeSummaryData[t]) {
          summaryMap[t] = tribeSummaryData[t];
        } else {
          summaryMap[t] = { error: 'No data for this tribe' };
        }
      });
      setTribeSummaries(summaryMap);
    } catch (err) {
      console.error('[ProdPerformance] Error fetching tribe summaries:', err);
      setTribeSummaryError('Unable to load tribe metrics.');
    } finally {
      setIsTribeSummaryLoading(false);
    }
  }, []);

  // Auto-load tribe summaries on mount and when time range changes
  useEffect(() => {
    if (timeRange) {
      fetchTribeSummaries(timeRange);
      // Clear drill-down when time period changes
      setSelectedTribe('');
      setSelectedOffering('');
      setOfferings([]);
      setPerformanceData(null);
      setDrillDownError(null);
    }
  }, [timeRange, fetchTribeSummaries]);

  // Update offerings list when tribe changes
  useEffect(() => {
    if (selectedTribe) {
      setOfferings(getOfferingsForTribe(selectedTribe));
    } else {
      setOfferings([]);
    }
    setSelectedOffering('');
    setPerformanceData(null);
    setDrillDownError(null);
  }, [selectedTribe]);

  // Fetch drill-down analysis when offering is selected
  const handleAnalyze = async () => {
    if (!selectedTribe || !timeRange) {
      setDrillDownError('Please select a Tribe and Time Period.');
      return;
    }

    setIsDrillDownLoading(true);
    setDrillDownError(null);
    setPerformanceData(null);

    try {
      if (selectedOffering) {
        // Service offering level analysis
        const serviceOffering = getServiceOfferingValue(selectedTribe, selectedOffering);

        // Step 1: Check cache
        const cachedResult = await graphqlQuery(getProdPerformance, {
          product: serviceOffering,
          timeline: timeRange,
        });

        if (cachedResult?.getProdPerformance?.bedrock_analysis) {
          const cachedPayload = parseJsonSafely(cachedResult.getProdPerformance.bedrock_analysis);
          const cachedError = getAnalysisError(cachedPayload);
          if (cachedError) { setDrillDownError(cachedError); return; }
          const normalized = normalizeAnalysis(cachedPayload);
          if (!normalized) { setDrillDownError('Cached performance data is invalid.'); return; }
          setPerformanceData(normalized);
          return;
        }

        // Step 2: Cache miss — invoke Lambda
        const freshResult = await graphqlMutation(analyzeProdPerformance, {
          input: { service_offering: serviceOffering, time_range: timeRange },
        });
        const responseBody = freshResult?.analyzeProdPerformance?.body;
        if (responseBody) {
          const parsed = parseJsonSafely(responseBody);
          const analysisError = getAnalysisError(parsed);
          if (analysisError) { setDrillDownError(analysisError); return; }
          const normalized = normalizeAnalysis(parsed);
          if (!normalized) { setDrillDownError('No usable performance data returned.'); return; }
          setPerformanceData(normalized);
        } else {
          setDrillDownError('No performance data returned.');
        }
      } else {
        // Tribe-level analysis (no specific service offering)
        const cachedTribeResult = await graphqlQuery(getTribePerformance, {
          tribe: selectedTribe,
          timeline: timeRange,
        });
        const cachedTribe = cachedTribeResult?.getTribePerformance;

        if (cachedTribe?.bedrock_analysis) {
          const cachedPayload = parseJsonSafely(cachedTribe.bedrock_analysis);
          const cachedError = getAnalysisError(cachedPayload);
          if (cachedError) { setDrillDownError(cachedError); return; }
          const normalized = normalizeAnalysis(cachedPayload);
          if (!normalized) { setDrillDownError('Cached tribe data is invalid.'); return; }
          setPerformanceData(normalized);
          return;
        }

        // Cache miss — invoke tribe analysis Lambda
        const freshTribeResult = await graphqlMutation(analyzeTribePerformance, {
          input: { tribe: selectedTribe, time_range: timeRange },
        });
        const responseBody = freshTribeResult?.analyzeTribePerformance?.body;
        if (responseBody) {
          const parsed = parseJsonSafely(responseBody);
          const analysisError = getAnalysisError(parsed);
          if (analysisError) { setDrillDownError(analysisError); return; }
          const normalized = normalizeAnalysis(parsed);
          if (!normalized) { setDrillDownError('No usable tribe data returned.'); return; }
          setPerformanceData(normalized);
        } else {
          setDrillDownError('No tribe performance data returned.');
        }
      }
    } catch (err) {
      console.error('[ProdPerformance] Error fetching drill-down:', err);
      setDrillDownError(err.message || 'Failed to fetch performance data.');
    } finally {
      setIsDrillDownLoading(false);
    }
  };

  // --- Render: Tribe Summary Cards ---
  const renderTribeSummaryCards = () => {
    if (isTribeSummaryLoading) {
      return (
        <div className="card mb-4">
          <div className="card-header"><h5 className="mb-0">Tribe Performance Overview</h5></div>
          <div className="card-body">
            <div className="row">
              {TRIBES.map((t) => (
                <div key={t} className="col-lg-4 col-md-6 mb-3">
                  <div className="card h-100" style={{ borderRadius: '12px' }}>
                    <div className="card-body">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6 mb-2"></span>
                        <span className="placeholder col-8"></span>
                        <span className="placeholder col-5"></span>
                        <span className="placeholder col-7"></span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (tribeSummaryError) {
      return (
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{tribeSummaryError}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => fetchTribeSummaries(timeRange)}>
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Tribe Performance Overview</h5></div>
        <div className="card-body">
          <div className="row">
            {TRIBES.map((t) => {
              const summary = tribeSummaries[t];
              const hasError = !summary || summary?.error;
              const totalInc = hasError ? '—' : (summary.total_incidents ?? 0);
              const avgRes = hasError ? '—' : formatResolutionTime(summary.avg_resolution_time_hours);
              const slaBreach = hasError ? '—' : (summary.sla_breached_count ?? 0);
              const recurring = hasError ? '—' : (summary.recurring_incident_count ?? 0);

              return (
                <div key={t} className="col-lg-4 col-md-6 mb-3">
                  <div className="card h-100" style={{
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}>
                    <div className="card-body">
                      <h6 className="card-title fw-bold mb-3" style={{ fontSize: '1.1rem' }}>{t}</h6>
                      {hasError ? (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Unable to load data</p>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-bar-chart-fill text-primary"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>{totalInc}</span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>Incidents</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-clock text-info"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>{avgRes}</span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>Avg Resolve Time</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill" style={{ color: getSlaColor(typeof slaBreach === 'number' ? slaBreach : 0) }}></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>{slaBreach}</span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>SLAs Breached</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-arrow-repeat text-secondary"></i>
                            <span className="fw-semibold" style={{ fontSize: '1.1rem' }}>{recurring}</span>
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>Recurring Incidents</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // --- Render: Drill-Down Detail ---
  const renderDrillDownDetail = () => {
    if (!performanceData) return null;

    const totalIncidents = performanceData?.total_incidents ?? 0;
    const topCountries = Array.isArray(performanceData?.top_5_countries)
      ? performanceData.top_5_countries : [];
    const recurringIncidents = Array.isArray(performanceData?.top_3_recurring_incidents)
      ? performanceData.top_3_recurring_incidents : [];
    const longestResolveTimes = Array.isArray(performanceData?.top_5_longest_resolve_times)
      ? performanceData.top_5_longest_resolve_times : [];

    // Aggregate countries by name
    const countryAggregation = {};
    topCountries.forEach((item) => {
      // The API may return country as a 2-char code prefix, full code, or full name
      const rawCountry = item.country || item.name || '';
      const code = rawCountry.slice(0, 2).toUpperCase();
      const name = countryCodeMapping[code] || rawCountry;
      // incident_count may come as different field names
      const count = item.incident_count || item.count || item.incidents || item.total || 0;
      countryAggregation[name] = (countryAggregation[name] || 0) + count;
    });
    const combinedCountries = Object.entries(countryAggregation)
      .map(([name, count]) => ({ country: name, incident_count: count }))
      .sort((a, b) => b.incident_count - a.incident_count)
      .slice(0, 5);
    const maxCountryCount = combinedCountries.length
      ? Math.max(...combinedCountries.map((c) => c.incident_count)) : 0;

    return (
      <>
        {/* KPI Cards */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card text-white text-center h-100" style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}>
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <h6 className="card-title mb-1">Total Incidents</h6>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.2 }}>{totalIncidents}</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-white text-center h-100" style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}>
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <h6 className="card-title mb-1">Top Country</h6>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {combinedCountries[0]?.country || '—'}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-white text-center h-100" style={{ backgroundColor: '#1C64F2', border: 'none', borderRadius: '12px' }}>
              <div className="card-body py-4 d-flex flex-column justify-content-center">
                <a
                  href="https://app.powerbi.com/groups/me/reports/e268437e-e7a5-42e3-a16f-ea105d757161/ad177848efe0fa9016b2?experience=power-bi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kone-btn kone-btn-white"
                >
                  View Ops In-Depth Analysis
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Countries + Top 3 Recurring */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header"><h6 className="mb-0">Top 5 Countries by Incident Count</h6></div>
              <div className="card-body">
                {combinedCountries.length === 0 && (
                  <p className="text-muted mb-0">No country data available for the selected period.</p>
                )}
                {combinedCountries.map((item) => (
                  <div key={item.country} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <strong>{item.country}</strong>
                      <span>{item.incident_count}</span>
                    </div>
                    <div style={{ background: '#E5E7EB', borderRadius: '4px', height: '22px' }}>
                      <div style={{
                        width: `${maxCountryCount > 0 ? (item.incident_count / maxCountryCount) * 100 : 0}%`,
                        background: '#1C64F2', height: '100%', borderRadius: '4px',
                        minWidth: '20px', transition: 'width 0.4s ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header"><h6 className="mb-0">Top 3 Recurring Incidents</h6></div>
              <div className="card-body">
                <ul className="list-group list-group-flush">
                  {recurringIncidents.length === 0 && (
                    <li className="list-group-item text-muted">No recurring incident data available.</li>
                  )}
                  {recurringIncidents.map((item, idx) => (
                    <li key={idx} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <strong>{item.type || item.incident_type || '—'}</strong>
                        <span className="badge ms-2" style={{ backgroundColor: '#DC2626' }}>
                          {item.count ?? item.frequency ?? 0}
                        </span>
                      </div>
                      {item.priority && <small className="text-muted d-block">Priority: {item.priority}</small>}
                      {Array.isArray(item.incident_numbers) && item.incident_numbers.length > 0 ? (
                        <small className="text-muted d-block mt-1">
                          <strong>Incidents:</strong> {item.incident_numbers.join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted d-block mt-1"><strong>Incidents:</strong> N/A</small>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Longest Resolve Times */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header"><h6 className="mb-0">Top 5 Longest Resolution Times</h6></div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Incident Number</th>
                        <th>Resolve Time</th>
                        <th>Short Description</th>
                        <th>Priority</th>
                        <th>SLA Target</th>
                        <th>SLA Breached</th>
                      </tr>
                    </thead>
                    <tbody>
                      {longestResolveTimes.length === 0 && (
                        <tr><td colSpan="6" className="text-center text-muted py-3">No resolve time data available.</td></tr>
                      )}
                      {longestResolveTimes.map((item, idx) => {
                        const incNumber = item.incident_number || item.number;
                        let shortDesc = item.short_description || item.description;
                        if (!shortDesc) {
                          const match = recurringIncidents.find(ri =>
                            Array.isArray(ri.incidents) && ri.incidents.includes(incNumber)
                          );
                          if (match) shortDesc = match.description;
                        }
                        return (
                          <tr key={incNumber || idx}>
                            <td><strong>{incNumber || '—'}</strong></td>
                            <td>{item.resolve_time || '—'}</td>
                            <td>{shortDesc || '—'}</td>
                            <td>{item.priority || 'N/A'}</td>
                            <td>{item.sla_target || 'N/A'}</td>
                            <td>
                              {item.sla_breached == null ? 'N/A' : item.sla_breached
                                ? <span className="badge bg-danger">Yes</span>
                                : <span className="badge bg-success">No</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // --- Main Render ---
  return (
    <div className="page-container">
      {isPermissionsLoading && (
        <div className="text-center mb-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Checking permissions...</p>
        </div>
      )}

      {!isPermissionsLoading && !canAccessProdPerformance() && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p className="mb-1">Your role does not have access to production performance.</p>
          <p className="mb-0"><strong>Current role:</strong> {getUserRole()}</p>
        </div>
      )}

      {!isPermissionsLoading && canAccessProdPerformance() && (
        <>
          <h2 className="kone-section-title mb-4">Prod Performance</h2>

          {/* Section 1: Time Period Filter */}
          <div className="card mb-4">
            <div className="card-header"><h5 className="mb-0">Select Duration</h5></div>
            <div className="card-body">
              <div className="row text-center align-items-center g-3">
                {TIME_RANGES.map((tr) => (
                  <div className="col-md-3 col-6" key={tr.value}>
                    <label
                      htmlFor={`duration-${tr.value}`}
                      className="d-inline-flex align-items-center justify-content-center gap-2 mb-0"
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        className="form-check-input mt-0"
                        type="radio"
                        name="prod-perf-duration"
                        id={`duration-${tr.value}`}
                        value={tr.value}
                        checked={timeRange === tr.value}
                        onChange={(e) => setTimeRange(e.target.value)}
                        style={{ width: '18px', height: '18px', flex: '0 0 auto', borderRadius: '50%' }}
                      />
                      <span>{tr.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Tribe Summary Cards */}
          {renderTribeSummaryCards()}

          {/* Section 3: Drill-Down */}
          <div className="card mb-4">
            <div className="card-header"><h5 className="mb-0">Analyze Tribe/Service Offering</h5></div>
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label htmlFor="drilldown-tribe" className="kone-label">Select Tribe</label>
                  <select
                    id="drilldown-tribe"
                    className="kone-select"
                    value={selectedTribe}
                    onChange={(e) => setSelectedTribe(e.target.value)}
                  >
                    <option value="">Select Tribe</option>
                    {TRIBES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="drilldown-offering" className="kone-label">Select Service Offering</label>
                  <select
                    id="drilldown-offering"
                    className="kone-select"
                    value={selectedOffering}
                    onChange={(e) => setSelectedOffering(e.target.value)}
                    disabled={!selectedTribe}
                  >
                    <option value="">Select offering...</option>
                    {offerings.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <button
                    className="kone-btn kone-btn-primary w-100"
                    onClick={handleAnalyze}
                    disabled={isDrillDownLoading || !selectedTribe || !timeRange}
                  >
                    {isDrillDownLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Analyzing...
                      </>
                    ) : selectedOffering ? (
                      'Analyze this Service Offering'
                    ) : (
                      'Analyze this Tribe'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Drill-Down Error */}
          {drillDownError && (
            <div className="alert alert-danger" role="alert">{drillDownError}</div>
          )}

          {/* Drill-Down Loading */}
          {isDrillDownLoading && (
            <div className="card mb-4">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-1">Analyzing incidents with AI...</p>
                <small className="text-muted">This may take 30–90 seconds for uncached data</small>
              </div>
            </div>
          )}

          {/* Drill-Down Detail */}
          {renderDrillDownDetail()}
        </>
      )}
    </div>
  );
}

export default ProdProductPerformance;
